import { createSelector } from 'reselect';
import {
  prop,
  filter,
  includes,
  values,
  propOr,
  slice,
  compose,
  equals,
  reject,
  propEq,
  path,
  isNil,
  always,
  when,
  length,
  uniqBy,
} from 'ramda';
import { selectExperiences, selectAllExperiences, selectMarkets } from '../contentRegistry';
import { ROUTES } from '../../routes/routes';
import {
  selectCurrentKioskHomeRoute,
  selectIsKioskMode,
  selectCurrentKioskName,
  selectCurrentKioskSlug,
  selectCurrentKioskExperiences,
} from '../kiosk';
import { extract } from '../../shared/utils/functools';
import { SIMILAR_EXPERIENCES_COUNT } from './experience.constants';

export const selectExperienceDomain = prop('experience');

export const selectCurrentExperienceId = createSelector(
  selectExperienceDomain,
  prop('currentExperienceId')
);

export const selectCurrentExperience = createSelector(
  selectCurrentExperienceId,
  selectAllExperiences,
  prop
);

export const selectCurrentExperienceSlug = createSelector(
  selectCurrentExperience,
  prop('slug')
);

export const selectCurrentExperienceDiscountPercent = createSelector(
  selectCurrentExperience,
  prop('discountPercent')
);

export const selectCurrentExperienceMarketId = createSelector(
  selectCurrentExperience,
  prop('market')
);

export const selectCurrentExperienceMarketData = createSelector(
  selectCurrentExperienceMarketId,
  selectMarkets,
  prop
);

export const selectCurrentExperienceMarketSlug = createSelector(
  selectCurrentExperienceMarketData,
  prop('slug')
);

export const selectCurrentExperienceMarketHomeRoute = createSelector(
  selectCurrentExperienceMarketSlug,
  slug => ROUTES.market(slug)
);

export const selectCurrentExperienceMarketName = createSelector(
  selectCurrentExperienceMarketData,
  prop('name')
);

export const selectCurrentExperienceTimezone = createSelector(
  selectCurrentExperienceMarketData,
  prop('timeZone')
);

export const selectCurrentExperienceHomeRoute = createSelector(
  selectCurrentExperienceSlug,
  selectIsKioskMode,
  selectCurrentKioskSlug,
  selectCurrentExperienceMarketSlug,
  (experienceSlug, isKiosk, kioskSlug, marketSlug) => {
    return isKiosk
      ? ROUTES.kioskExperience(kioskSlug, experienceSlug)
      : ROUTES.marketExperience(marketSlug, experienceSlug);
  }
);

export const selectExperienceTicketTypes = createSelector(
  selectCurrentExperience,
  path(['tickets', 'types'])
);

export const selectSameKioskExperiences = selectCurrentKioskExperiences;

export const selectSameMarketExperiences = createSelector(
  selectExperiences,
  selectCurrentExperienceMarketId,
  (allExperiences, currentMarket) => filter(propEq('market', currentMarket), values(allExperiences))
);

export const selectSameContextExperiences = createSelector(
  selectIsKioskMode,
  selectSameMarketExperiences,
  selectSameKioskExperiences,
  (isKiosk, sameMarketExperiences, sameKioskExperiences) => {
    return isKiosk ? sameKioskExperiences : sameMarketExperiences;
  }
);

export const selectSameContextExperiencesCount = createSelector(
  selectSameContextExperiences,
  length
);

export const selectSimilarExperiences = createSelector(
  selectCurrentExperienceId,
  selectCurrentExperience,
  selectSameContextExperiences,
  (currentExperienceId, currentExperience, sameContextExperiences) => {
    const currentCategories = propOr([], 'category', currentExperience);

    const sameCategoryExperiences = filter(
      ({ category = [] }) => currentCategories.some(activeCategory => includes(activeCategory, category)),
      sameContextExperiences
    );

    return compose(
      slice(0, SIMILAR_EXPERIENCES_COUNT),
      uniqBy(prop('id')),
      reject(propEq('id', currentExperienceId))
    )([...sameCategoryExperiences, ...sameContextExperiences]);
  }
);

export const selectExperienceGroupLimits = createSelector(
  selectCurrentExperience,
  extract({
    min: propOr(0, 'minGroupSize'),
    max: propOr(Number.MAX_VALUE, 'maxGroupSize'),
  })
);

export const selectExperienceAgeLimits = createSelector(
  selectCurrentExperience,
  ({ maxChildAge, maxInfantAge }) => {
    const infant = maxInfantAge ? [0, maxInfantAge] : null;
    const adult = maxChildAge ? [maxChildAge + 1] : null;

    const childMin = maxInfantAge ? maxInfantAge + 1 : 0;
    const child = compose(
      when(equals([0]), always(null)),
      reject(isNil)
    )([childMin, maxChildAge]);

    return {
      infant,
      child,
      adult,
    };
  }
);

export const selectExperienceContextHomeRoute = createSelector(
  selectIsKioskMode,
  selectCurrentKioskHomeRoute,
  selectCurrentExperienceMarketHomeRoute,
  (isKiosk, kioskRoute, marketRoute) => {
    return isKiosk ? kioskRoute : marketRoute;
  }
);

export const selectExperienceContextHomeName = createSelector(
  selectIsKioskMode,
  selectCurrentKioskName,
  selectCurrentExperienceMarketName,
  (isKiosk, kioskName, marketName) => {
    return isKiosk ? kioskName : marketName;
  }
);
